import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Quote, Users } from 'lucide-react';
import MeshHero from '@/components/saas/MeshHero';
import Reveal from '@/components/saas/Reveal';
import MarketingCtaBand from '@/components/saas/MarketingCtaBand';
import { parseMarketingSections } from '@/lib/parse-marketing-html';
import { getCtaBanner, type HomepageSection } from './helpers';
import ProseFallback from './ProseFallback';

interface Props {
  page: { title?: string; content?: string };
  sections?: HomepageSection[];
  siteName: string;
}

export default function TenantCustomersPage({ page, sections, siteName }: Props) {
  const { t } = useTranslation('saas');
  const parsed = useMemo(() => parseMarketingSections(page.content), [page.content]);
  const cta = getCtaBanner(sections);

  const hasPersonas = parsed.sections.length > 0;
  const heading = page.title || t('tenantMarketing.customersSubheadingFallback', { name: siteName });
  // Promote the lead paragraph to the hero only when persona cards follow;
  // otherwise the prose fallback renders the same text below.
  const subheading = hasPersonas
    ? parsed.intro[0] || t('tenantMarketing.customersSubheadingFallback', { name: siteName })
    : t('tenantMarketing.customersSubheadingFallback', { name: siteName });

  const spotlight = parsed.sections[0];
  const rest = parsed.sections.slice(1);
  const isEmpty = parsed.sections.length === 0 && parsed.intro.length === 0;

  return (
    <>
      <MeshHero
        heading={heading}
        subheading={subheading}
        primaryCta={{ label: t('tenantMarketing.getStarted'), to: cta.ctaLink || '/signup' }}
        secondaryCta={{ label: t('tenantMarketing.viewFeatures'), to: '/features' }}
      />

      {isEmpty ? (
        <section className="bg-surface">
          <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sage-50 to-cream p-10 text-center ring-1 ring-sage-200 lg:p-14">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-sage-200/40 blur-3xl"
                />
                <div className="relative">
                  <div className="inline-flex rounded-2xl bg-gradient-to-br from-sage-100 to-cream p-3 ring-1 ring-sage-200">
                    <Quote className="h-7 w-7 text-sage-300" aria-hidden="true" />
                  </div>
                  <h2 className="mt-4 font-display text-2xl font-bold text-sage-900 sm:text-3xl">
                    {t('tenantMarketing.customersComingSoonHeading')}
                  </h2>
                  <p className="mt-4 leading-relaxed text-sage-700">
                    {t('tenantMarketing.customersComingSoonBody', { name: siteName })}
                  </p>
                  <Link
                    to="/signup"
                    className="mt-6 inline-flex items-center gap-2 font-semibold text-sage-700 underline-offset-4 transition-colors hover:text-sage-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                  >
                    {t('tenantMarketing.getStarted')}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      ) : parsed.sections.length === 0 ? (
        <ProseFallback html={page.content || ''} />
      ) : (
        <section className="bg-surface">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            {spotlight && (
              <Reveal>
                <article className="rounded-3xl bg-gradient-to-br from-cream to-surface p-8 shadow-sm ring-1 ring-sage-200 lg:p-12">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-sage-100 ring-1 ring-sage-200">
                      <Users className="h-5 w-5 text-sage-700" aria-hidden="true" />
                    </div>
                    <span className="text-sm font-semibold uppercase tracking-wide text-sage-600">
                      {spotlight.title}
                    </span>
                  </div>
                  {spotlight.body.map((p, i) => (
                    <p
                      key={i}
                      className={i === 0 ? 'mt-4 font-display text-xl leading-relaxed text-sage-800 sm:text-2xl' : 'mt-4 leading-relaxed text-sage-700'}
                    >
                      {p}
                    </p>
                  ))}
                </article>
              </Reveal>
            )}

            {rest.length > 0 && (
              <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((persona, i) => (
                  <Reveal key={`${persona.title}-${i}`} delayMs={(i % 3) * 80} className="h-full">
                    <article className="group flex h-full min-w-0 flex-col rounded-2xl border border-sage-200 bg-surface p-6 transition-all duration-200 hover:-translate-y-1 hover:border-sage-400 hover:shadow-lg focus-within:ring-2 focus-within:ring-sage-600 motion-reduce:hover:translate-y-0 lg:p-8">
                      <div className="inline-flex w-fit rounded-full bg-sage-100 p-2.5 ring-1 ring-sage-200">
                        <Users className="h-5 w-5 text-sage-700" aria-hidden="true" />
                      </div>
                      <h3 className="mt-4 font-display text-lg font-semibold text-sage-900">{persona.title}</h3>
                      {persona.body.map((p, j) => (
                        <p key={j} className="mt-2 leading-relaxed text-sage-600">
                          {p}
                        </p>
                      ))}
                    </article>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <MarketingCtaBand
        heading={cta.heading || t('tenantMarketing.ctaHeadingFallback')}
        subheading={cta.subheading || t('tenantMarketing.ctaSubheadingFallback', { name: siteName })}
        primaryLabel={cta.ctaText || t('tenantMarketing.getStarted')}
        primaryTo={cta.ctaLink || '/signup'}
        secondaryLabel={t('tenantMarketing.viewFeatures')}
        secondaryTo="/features"
      />
    </>
  );
}
